import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import { useControllable } from '../../hooks/useControllable';
import styles from './Collapse.module.css';

const collapse = cva(styles.base, {
  variants: {
    ghost: { true: styles.ghost },
  },
});

interface CollapsePanelProps {
  key: string;
  header: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  extra?: React.ReactNode;
  /** Number of items — shown as count badge on the header */
  count?: number;
}

interface CollapseProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled active panel keys */
  activeKey?: string | string[];
  /** Default active keys */
  defaultActiveKey?: string | string[];
  /** Accordion mode (only one open) */
  accordion?: boolean;
  /** Ghost mode (no background/border) */
  ghost?: boolean;
  /** Compact mode — smaller padding, no content padding */
  compact?: boolean;
  /** Expand icon position */
  expandIconPosition?: 'start' | 'end';
  /** Custom expand icon render — receives { isOpen, key } */
  expandIcon?: (props: { isOpen: boolean; panelKey: string }) => React.ReactNode;
  /** Show extra only on hover */
  showExtraOnHover?: boolean;
  /** Change handler */
  onChange?: (keys: string[]) => void;
  /** Gap between panels */
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  /** Panel items */
  items: CollapsePanelProps[];
}

const gapTokenMap: Record<string, string> = {
  xs: 'var(--space-1)',
  sm: 'var(--space-2)',
  md: 'var(--space-3)',
  lg: 'var(--space-5)',
};

/**
 * Collapse — expandable accordion sections.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | activeKey | string \| string[] | — | Controlled open panels |
 * | defaultActiveKey | string \| string[] | [] | Default open |
 * | accordion | boolean | false | Only one panel open |
 * | ghost | boolean | false | No background/border |
 * | compact | boolean | false | Minimal padding |
 * | expandIconPosition | 'start' \| 'end' | 'start' | Icon position |
 * | expandIcon | (props) => ReactNode | — | Custom expand icon renderer |
 * | showExtraOnHover | boolean | false | Extra only visible on hover |
 * | onChange | (keys) => void | — | Change handler |
 * | items | CollapsePanelProps[] | — | Panel definitions |
 *
 * @example
 * <Collapse items={[
 *   { key: '1', header: 'Section 1', children: <p>Content 1</p> },
 *   { key: '2', header: 'Section 2', children: <p>Content 2</p> },
 * ]} />
 */
export function Collapse({
  activeKey: controlledKey,
  defaultActiveKey = [],
  accordion = false,
  ghost = false,
  compact = false,
  expandIconPosition = 'start',
  expandIcon,
  showExtraOnHover = false,
  gap,
  onChange,
  items,
  className,
  style,
  ...props
}: CollapseProps) {
  const normalize = (v: string | string[]) => (Array.isArray(v) ? v : [v]);
  const [activeKeys, setActiveKeys] = useControllable(
    controlledKey !== undefined ? normalize(controlledKey) : undefined,
    normalize(defaultActiveKey),
    onChange,
  );

  const toggle = (key: string) => {
    if (accordion) {
      setActiveKeys(activeKeys.includes(key) ? [] : [key]);
    } else {
      setActiveKeys(activeKeys.includes(key) ? activeKeys.filter((k) => k !== key) : [...activeKeys, key]);
    }
  };

  // Combine gap token (CSS custom property) with user-provided style
  const combinedStyle: React.CSSProperties | undefined = gap
    ? { '--collapse-gap': gapTokenMap[gap], ...style } as React.CSSProperties
    : style;

  return (
    <div
      className={clsx(collapse({ ghost }), gap && styles.withGap, className)}
      style={combinedStyle}
      {...props}
    >
      {items.map((panel, i) => {
        const isOpen = activeKeys.includes(panel.key);
        return (
          <CollapseItem
            key={panel.key}
            panel={panel}
            open={isOpen}
            ghost={ghost}
            compact={compact}
            expandIconPosition={expandIconPosition}
            expandIcon={expandIcon}
            showExtraOnHover={showExtraOnHover}
            isFirst={i === 0}
            onToggle={() => !panel.disabled && toggle(panel.key)}
          />
        );
      })}
    </div>
  );
}

function CollapseItem({
  panel,
  open,
  ghost,
  compact,
  expandIconPosition,
  expandIcon,
  showExtraOnHover,
  isFirst,
  onToggle,
}: {
  panel: CollapsePanelProps;
  open: boolean;
  ghost: boolean;
  compact: boolean;
  expandIconPosition: 'start' | 'end';
  expandIcon?: (props: { isOpen: boolean; panelKey: string }) => React.ReactNode;
  showExtraOnHover: boolean;
  isFirst: boolean;
  onToggle: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);

  const chevron = expandIcon ? (
    expandIcon({ isOpen: open, panelKey: panel.key })
  ) : (
    <motion.div
      animate={{ rotate: open ? 90 : 0 }}
      transition={{ duration: duration.normal / 1000 }}
      className={styles.chevron}
    >
      <Icon
        name="chevron_right"
        size={compact ? 12 : 16}
        weight={300}
        className={styles.chevronIcon}
      />
    </motion.div>
  );

  return (
    <div className={clsx(styles.item, !isFirst && !ghost && styles.itemBordered)}>
      {/* Header button */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={clsx(
          styles.header,
          ghost && styles.headerGhost,
          compact && styles.headerCompact,
          panel.disabled && styles.headerDisabled,
        )}
      >
        {/* Left: icon + header + count */}
        <span className={styles.headerLeft}>
          {expandIconPosition === 'start' && chevron}
          <span className={styles.headerTitle}>
            {panel.header}
          </span>
          {panel.count !== undefined && panel.count > 0 && (
            <span className={styles.count}>
              {panel.count}
            </span>
          )}
        </span>

        {/* Right: extra + chevron (if end position) */}
        <span className={styles.headerRight}>
          {panel.extra && (
            <span
              className={showExtraOnHover ? (hovered ? styles.extraVisible : styles.extraHidden) : styles.extraVisible}
              onClick={(e) => e.stopPropagation()}
            >
              {panel.extra}
            </span>
          )}
          {expandIconPosition === 'end' && chevron}
        </span>
      </div>

      {/* Expandable content */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: duration.slow / 1000 }}
            className={styles.contentOverflow}
          >
            <div
              className={clsx(
                styles.content,
                compact && styles.contentCompact,
                ghost && styles.contentGhost,
              )}
            >
              {panel.children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
